import {
  surfaceKeyPrefix,
  surfaceObjectKey,
  type UiBox,
  type UiPoint,
  type UiSemanticElement,
  WORKSPACE_OPERATION_MAX_SCROLL,
  type WorkspaceSurfaceContent,
  type WorkspaceViewScope,
} from '@streamlain/copilot-realtime-contract';

import type { View } from '../../workbench/entities/view';
import { contains, hitAt } from './operation-input';
import { readSemanticDomWithin } from './semantic-dom-context';
import { classifyUiAccess } from './ui-access-rules';
import type {
  WorkspaceSurfaceOptions,
  WorkspaceViewContextService,
} from './workspace-view-context';

/** One object a surface draws itself (canvas element, graph node, page), as drawn now. */
export interface SurfaceObject {
  /** Stable within its surface. */
  id: string;
  /** What the object is, e.g. `shape`, `note`, `node`, `page`. */
  role: string;
  label: string;
  text?: string;
  selected?: boolean;
  /** Where it is drawn, in viewport pixels. */
  box: UiBox;
}

/**
 * The one adapter a drawn surface type provides: its root and the objects it
 * draws. Objects are operated through pointer input on the root, the same way
 * the user operates them, so a new object kind needs no adapter change.
 */
export interface SurfaceObjectSource {
  /** What the surface is, e.g. `canvas`, `graph`, `document`. */
  role: string;
  label: string;
  /** The element the surface draws into and takes pointer input on. */
  root(): Element | null;
  /**
   * The drawn objects, front-most first. What lies outside the view may follow:
   * the surface names the nearest of it, so no more than `MAX_BEYOND_OBJECTS`
   * and one are worth reading.
   */
  objects(): Iterable<SurfaceObject>;
  /** One object as drawn now, or null once it is gone. */
  object(id: string): SurfaceObject | null;
  /**
   * The elements that show an object's text now, in reading order, whether or
   * not it is open for typing: where text in it is selected. The surface as a
   * whole shows the text of everything it draws.
   */
  readableTexts?(id: string): readonly Element[];
  /**
   * The element an object draws controls of its own in while a click reaches them (a table in
   * a block: its views, its columns, its cells), or null while it draws none. A read lists
   * them for a selected object.
   */
  controlsOf?(id: string): Element | null;
  /**
   * Whether a press at a point lands on an object, where the surface stacks what it draws: an
   * object takes no press where another lies over it. Without it every point of an object's
   * box is its own.
   */
  owns?(id: string, point: UiPoint): boolean;
  /**
   * How many viewport pixels one unit of the surface's own coordinates takes now (a canvas drawn
   * at 27 % zoom: 0.27). A surface whose tools and user speak in units of its own names it, so a
   * distance in those units becomes the pixels a drag moves.
   */
  zoom?(): number | null;
}

/**
 * The one of several surfaces that is drawn now, as one surface: a document
 * shows either its canvas or its page.
 */
export function shownSurface(
  ...sources: readonly SurfaceObjectSource[]
): SurfaceObjectSource {
  const shown = () => sources.find(source => source.root()) ?? sources[0];
  return {
    get role() {
      return shown().role;
    },
    get label() {
      return shown().label;
    },
    root: () => shown().root(),
    objects: () => shown().objects(),
    object: id => shown().object(id),
    readableTexts: id => shown().readableTexts?.(id) ?? [],
    controlsOf: id => shown().controlsOf?.(id) ?? null,
    owns: (id, point) => shown().owns?.(id, point) ?? true,
    zoom: () => shown().zoom?.() ?? null,
  };
}

/** A surface object resolved for an operation: where pointer input goes and what it is now. */
export interface SurfaceObjectTarget {
  root: Element;
  element: UiSemanticElement;
  /** The elements that show its text now, open for typing or not. */
  readable(): readonly Element[];
  /** Whether a press at a point lands on it; the surface itself takes a press anywhere. */
  owns(point: UiPoint): boolean;
}

/** Objects one surface lists per read, leaving room for the rest of the view. */
const MAX_SURFACE_OBJECTS = 64;
/** Controls of its selected objects one surface lists per read, leaving room for the rest of the view. */
const MAX_OBJECT_CONTROLS = 32;
/** Objects outside the view a surface names per read: the nearest ones. */
export const MAX_BEYOND_OBJECTS = 12;
const MAX_TEXT_CODE_POINTS = 256;
/** What names an object outside the view: enough to tell it from the others. */
const MAX_BEYOND_LABEL_CODE_POINTS = 48;
const SURFACE_ID = '';

const clip = (text: string, codePoints = MAX_TEXT_CODE_POINTS) =>
  Array.from(text.replace(/\s+/gu, ' ').trim()).slice(0, codePoints).join('');

/** The visible part of a box within the surface and the viewport, in whole pixels. */
function visibleBox(box: UiBox, bounds: DOMRect): UiBox | null {
  const view = { width: window.innerWidth, height: window.innerHeight };
  const left = Math.max(box.x, bounds.left, 0);
  const top = Math.max(box.y, bounds.top, 0);
  const right = Math.min(box.x + box.width, bounds.right, view.width);
  const bottom = Math.min(box.y + box.height, bounds.bottom, view.height);
  if (!(right > left && bottom > top)) return null;
  const x = Math.ceil(left),
    y = Math.ceil(top);
  return {
    x,
    y,
    width: Math.max(0, Math.floor(right) - x),
    height: Math.max(0, Math.floor(bottom) - y),
  };
}

/** Lines per side a surface is probed along for what lies over it. */
const CLEAR_PROBE = { columns: 10, rows: 8 };

/** `count + 1` lines from just inside one edge of a side to just inside the other. */
const probeLines = (start: number, length: number, count: number) =>
  Array.from(
    { length: count + 1 },
    (_, index) => start + 1 + ((length - 2) * index) / count
  );

/**
 * The largest part of a surface's visible box that lies clear. What floats over a surface (the
 * chat window, a dock, a panel) takes the pointer there, so only a point of this part reaches
 * the surface: a free spot to click, or to let go of what is dragged onto it. A side nothing
 * lies over keeps its edge. A surface that lies clear nowhere keeps its box: its objects stay
 * listed, and an operation is refused at the point that is covered.
 */
function clearBox(root: Element, box: UiBox): UiBox {
  const document = root.ownerDocument;
  const xs = probeLines(box.x, box.width, CLEAR_PROBE.columns);
  const ys = probeLines(box.y, box.height, CLEAR_PROBE.rows);
  const clear = ys.map(y =>
    xs.map(x => {
      const hit = hitAt(document, { x, y });
      return !!hit && contains(root, hit);
    })
  );
  // The largest rectangle of cells whose four corners lie clear, row by row over the cells'
  // clear heights.
  const heights = new Array<number>(CLEAR_PROBE.columns).fill(0);
  let best = { area: 0, left: 0, right: 0, top: 0, bottom: 0 };
  for (let row = 0; row < CLEAR_PROBE.rows; row++) {
    for (let column = 0; column < CLEAR_PROBE.columns; column++)
      heights[column] =
        clear[row][column] &&
        clear[row][column + 1] &&
        clear[row + 1][column] &&
        clear[row + 1][column + 1]
          ? heights[column] + 1
          : 0;
    for (let left = 0; left < CLEAR_PROBE.columns; left++)
      for (
        let right = left, low = heights[left];
        right < CLEAR_PROBE.columns && low > 0;
        right++
      ) {
        low = Math.min(low, heights[right]);
        const area =
          (xs[right + 1] - xs[left]) * (ys[row + 1] - ys[row + 1 - low]);
        if (area > best.area)
          best = {
            area,
            left,
            right: right + 1,
            top: row + 1 - low,
            bottom: row + 1,
          };
      }
  }
  if (!best.area) return box;
  const left = best.left === 0 ? box.x : Math.ceil(xs[best.left]);
  const top = best.top === 0 ? box.y : Math.ceil(ys[best.top]);
  const right =
    best.right === CLEAR_PROBE.columns
      ? box.x + box.width
      : Math.floor(xs[best.right]);
  const bottom =
    best.bottom === CLEAR_PROBE.rows
      ? box.y + box.height
      : Math.floor(ys[best.bottom]);
  return { x: left, y: top, width: right - left, height: bottom - top };
}

const middle = (box: UiBox) => ({
  x: box.x + box.width / 2,
  y: box.y + box.height / 2,
});

/** Whether a box lies wholly outside of what a surface shows. */
const liesOutside = (box: UiBox, shown: UiBox) =>
  box.x > shown.x + shown.width ||
  box.x + box.width < shown.x ||
  box.y > shown.y + shown.height ||
  box.y + box.height < shown.y;

/** As far as one scroll goes towards a distance. */
const oneScroll = (distance: number) =>
  Math.max(
    -WORKSPACE_OPERATION_MAX_SCROLL,
    Math.min(WORKSPACE_OPERATION_MAX_SCROLL, Math.round(distance)) || 0
  );

/** The zoom a surface is drawn at, in words: a distance in its own units in viewport pixels. */
function zoomText(role: string, zoom: number | null | undefined): string {
  if (!zoom) return '';
  const pixels = Number(zoom.toFixed(3));
  return `Drawn at ${Math.round(zoom * 100)} % zoom: a distance in ${role} units (the units of the ${role} tools) is ${pixels} times as many viewport pixels.`;
}

/**
 * What a surface draws outside of what it shows, in words: the nearest objects,
 * each with the scroll of the surface that moves it to the middle of the part
 * that lies clear, as far as one scroll goes. The objects themselves are not
 * listed, they cannot be operated where they are; after the scroll a read
 * lists them. Empty when nothing lies outside.
 */
function beyondText(
  role: string,
  clear: UiBox,
  beyond: readonly SurfaceObject[]
): string {
  if (!beyond.length) return '';
  const centre = middle(clear);
  const nearest = beyond
    .map(object => {
      const at = middle(object.box);
      return { object, x: at.x - centre.x, y: at.y - centre.y };
    })
    .sort((a, b) => Math.hypot(a.x, a.y) - Math.hypot(b.x, b.y))
    .slice(0, MAX_BEYOND_OBJECTS)
    .map(({ object, x, y }) => {
      const label = clip(object.label, MAX_BEYOND_LABEL_CODE_POINTS);
      const by = JSON.stringify({ x: oneScroll(x), y: oneScroll(y) });
      return `${object.role} ${JSON.stringify(label)} by ${by}`;
    });
  const more = beyond.length > MAX_BEYOND_OBJECTS ? '; and more' : '';
  return `Outside the view, each with the scroll of this ${role} towards it: ${nearest.join('; ')}${more}.`;
}

function project(
  ownerId: string,
  root: Element,
  source: SurfaceObjectSource,
  object: SurfaceObject | null
): UiSemanticElement | null {
  const bounds = root.getBoundingClientRect();
  const visible = visibleBox(object?.box ?? bounds, bounds);
  if (!visible) return null;
  const box = object ? visible : clearBox(root, visible);
  // A surface sits in an area like every element and takes that area's access.
  const access = classifyUiAccess(root);
  return {
    key: surfaceObjectKey(ownerId, object?.id ?? SURFACE_ID),
    role: object?.role ?? source.role,
    label: clip(object?.label ?? source.label),
    text: clip(object?.text ?? ''),
    selected: object?.selected ?? false,
    disabled: false,
    refs: [],
    box,
    ...(access ? { access } : {}),
  };
}

/**
 * View-context options of one drawn surface: its visible objects with their
 * boxes plus the surface itself (to click free spots, e.g. after picking a dock
 * tool), read at request time, and the lookup an operation re-checks against.
 */
export function surfaceObjectContext(
  ownerId: string,
  source: SurfaceObjectSource,
  scope: WorkspaceViewScope
): Required<
  Pick<WorkspaceSurfaceOptions, 'collect' | 'locate' | 'surfaceRoot'>
> {
  const prefix = surfaceKeyPrefix(ownerId);
  const surfaceKey = surfaceObjectKey(ownerId, SURFACE_ID);
  return {
    surfaceRoot: () => source.root(),
    collect: async (_signal, _isCurrent, deadlineMs) => {
      const root = source.root();
      const surface = root?.isConnected && project(ownerId, root, source, null);
      if (!root || !surface) return { elements: [], interactions: [] };
      const bounds = root.getBoundingClientRect();
      // What the surface shows, and the part of it that lies clear.
      const shown = visibleBox(bounds, bounds) ?? bounds;
      const clear = surface.box ?? shown;
      const elements: UiSemanticElement[] = [];
      const controls: UiSemanticElement[] = [];
      const beyond: SurfaceObject[] = [];
      for (const object of source.objects()) {
        if (elements.length >= MAX_SURFACE_OBJECTS) break;
        const element = project(ownerId, root, source, object);
        if (!element) {
          if (liesOutside(object.box, shown)) beyond.push(object);
          continue;
        }
        elements.push(element);
        // A selected object takes a click on what it draws inside it. That is read from the
        // object itself: a read of the whole view ends long before it reaches one object among
        // all a surface draws.
        const drawn = object.selected ? source.controlsOf?.(object.id) : null;
        if (drawn && controls.length < MAX_OBJECT_CONTROLS)
          controls.push(
            ...readSemanticDomWithin(root, drawn, scope, deadlineMs).slice(
              0,
              MAX_OBJECT_CONTROLS - controls.length
            )
          );
      }
      // The surface itself comes first: a view cut short still reaches every spot on it.
      // Its text says how far its own units go in pixels, what it draws outside the view, and
      // how to bring that in.
      const text = [
        zoomText(source.role, source.zoom?.()),
        beyondText(source.role, clear, beyond),
      ]
        .filter(Boolean)
        .join(' ');
      return {
        elements: [{ ...surface, text }, ...elements, ...controls],
        interactions: [],
      };
    },
    locate: (key: string): SurfaceObjectTarget | null => {
      const root = source.root();
      if (!root?.isConnected) return null;
      if (!key.startsWith(prefix)) return null;
      const object =
        key === surfaceKey ? null : source.object(key.slice(prefix.length));
      if (key !== surfaceKey && !object) return null;
      const element = project(ownerId, root, source, object);
      return element
        ? {
            root,
            element,
            // The surface as a whole shows what everything it draws shows.
            readable: () =>
              (object ? [object] : Array.from(source.objects())).flatMap(
                drawn => source.readableTexts?.(drawn.id) ?? []
              ),
            owns: point => !object || (source.owns?.(object.id, point) ?? true),
          }
        : null;
    },
  };
}

/**
 * A drawn surface that is a view of its own (graph, PDF) in the view the AI
 * sees: its title and route, and its objects read at request time.
 */
export function bindSurfaceObjects(
  context: WorkspaceViewContextService,
  view: View,
  workspaceId: string,
  ownerId: string,
  source: SurfaceObjectSource
): () => void {
  const scope = { workspaceId, viewId: view.id };
  const pathname = view.location$.value.pathname;
  return context.register(
    scope,
    ownerId,
    (): WorkspaceSurfaceContent => {
      const location = view.location$.value;
      return {
        surfaceId: ownerId,
        route: location.pathname + location.search + location.hash,
        title: source.label,
        summary: '',
        refs: [],
        elements: [],
        interactions: [],
        actions: [],
      };
    },
    {
      kind: 'surface',
      // Navigation drops the surface before React's subsequent effect cleanup.
      isActive: () =>
        context.isActiveScope(scope) &&
        view.location$.value.pathname === pathname,
      ...surfaceObjectContext(ownerId, source, scope),
    }
  );
}
